import React, { createContext, useContext, useState, useEffect } from 'react';
import apiClient from '../api-wa/client';

export const ConfigContext = createContext();

export const ConfigProvider = ({ children }) => {
  const [configs, setConfigs] = useState([]);
  const [activeConfigId, setActiveConfigId] = useState(() => {
    return localStorage.getItem('activeWhatsAppConfigId') || '';
  });
  const [activeConfig, setActiveConfig] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/whatsapp/config/');
      const data = response.data?.results ?? response.data ?? [];
      setConfigs(data);
      
      // If there's an activeConfigId, find the matching config
      let matched = null;
      if (activeConfigId) {
        matched = data.find(c => String(c.id) === String(activeConfigId));
      }
      
      if (data.length === 0) {
        setActiveConfigId('');
        localStorage.removeItem('activeWhatsAppConfigId');
        setActiveConfig(null);
      } else if (!matched && data.length > 0) {
        matched = data[0];
        setActiveConfigId(String(matched.id));
        localStorage.setItem('activeWhatsAppConfigId', String(matched.id));
        setActiveConfig(matched);
        apiClient.post(`/whatsapp/config/${matched.id}/set_active/`).catch(err =>
          console.error('Failed to sync auto-selected active config:', err)
        );
      } else {
        setActiveConfig(matched);
      }
    } catch (err) {
      console.error('Failed to fetch WhatsApp configurations:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch configs on mount so inbox/campaigns work on direct page load or reload
  useEffect(() => {
    fetchConfigs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeConfigId && configs.length > 0) {
      const matched = configs.find(c => String(c.id) === String(activeConfigId));
      if (matched) {
        setActiveConfig(matched);
      }
    }
  }, [activeConfigId, configs]);

  const switchConfig = async (id) => {
    setActiveConfigId(String(id));
    localStorage.setItem('activeWhatsAppConfigId', String(id));
    const matched = configs.find(c => String(c.id) === String(id));
    if (matched) {
      setActiveConfig(matched);
    }
    
    // Broadcast a custom event so other components can listen and refresh
    window.dispatchEvent(new CustomEvent('configChanged', { detail: { id } }));

    // Sync active config to SaaS backend so SaaS portal redirections use the correct config
    try {
      await apiClient.post(`/whatsapp/config/${id}/set_active/`);
    } catch (err) {
      console.error('Failed to sync active config to backend:', err);
    }
  };

  // Config fetching is now exclusively handled by App.jsx when the user reaches the dashboard.
  // This eliminates the duplicate API request on page load.

  return (
    <ConfigContext.Provider value={{
      configs,
      activeConfigId,
      activeConfig,
      loading,
      fetchConfigs,
      switchConfig
    }}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

